import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { map, mergeMap, take } from 'rxjs/operators';
import { of, Subject, throwError, combineLatest } from 'rxjs';

import { NOTEBOOK_UPLOAD_CONFIG } from '@mode/shared/ui';
import { FileUploadDialogService } from '@mode/shared/ui';
import { NotebookFacade } from '@mode/notebooks/contract';
import { selectImporting, selectImportError } from '@mode/notebooks/domain';
import { filter, pairwise, startWith } from 'rxjs/operators';
import { NotebookStateService } from '@mode/notebooks/domain';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { Store } from '@ngrx/store';
@Component({
  selector: 'mode-notebook-import-modal',
  templateUrl: './notebook-import-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotebookImportModalComponent implements OnDestroy {
  NOTEBOOK_UPLOAD_CONFIG = NOTEBOOK_UPLOAD_CONFIG;
  importing$ = this.store.select(selectImporting);
  importError$ = this.store.select(selectImportError);
  private destroy$ = new Subject<void>();
  importNotebookEnabled$ = of(false);

  constructor(
    private fileUploadDialogService: FileUploadDialogService,
    private notebookFacade: NotebookFacade,
    private notebookStateService: NotebookStateService,
    private store: Store,
    private featureFlagFacade: FeatureFlagsFacade,
    @Inject('$state') private $state: any
  ) {
    const isAddNotebook$ = this.notebookStateService.notebookType$;
    const isImportEnabled$ = this.featureFlagFacade.asObservable(FeatureFlag.CreateNotebookWithNoQuery);
    this.importNotebookEnabled$ = combineLatest([isAddNotebook$, isImportEnabled$]).pipe(
      map(([type, isEnabled]) => isEnabled && !type)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUploadClicked(files: File[]) {
    if (!files || files.length === 0) return;
    this.notebookFacade.importNotebook(files[0]);
  }

  close(): void {
    this.fileUploadDialogService.close();
  }
  openImportModal() {
    this.fileUploadDialogService.open(
      {
        title: 'Upload a Notebook',
        config: NOTEBOOK_UPLOAD_CONFIG,
        selectButtonText: 'Click here to upload a file',
        dragMessage: 'Drag a .ipynb file into this window or',
        uploadButtonText: 'Upload',
        cancelButtonText: 'Cancel',
        showMaxFileSize: true,
      },
      (files) => {
        if (!files.length) return throwError(() => new Error('No file selected'));
        this.notebookFacade.importNotebook(files[0]);
        return this.importing$.pipe(
          startWith(false),
          pairwise(),
          filter(([prev, curr]) => prev && !curr),
          take(1),
          mergeMap(() =>
            this.importError$.pipe(
              take(1),
              mergeMap((err) => {
                if (!err) {
                  this.$state?.go('application.reports.edit.notebook');
                  return of({ success: true, files });
                } else {
                  return throwError(() => new Error(err));
                }
              })
            )
          )
        );
      }
    );
  }
}
